// source: template-project@7007a338a apps/backoffice/src/pages/onboarding-v2/QuestionCard.tsx (L111-L116 chip list, RankChip)
import { X } from 'lucide-react';

import { IconButton } from '@/components/ui/icon-button';

import type { Question, QuestionOption } from '../quiz/questions';

/**
 * Adds a typed entry to a ranking: the id of a listed option when the text names one (no duplicate), else the
 * text itself. Exclusive options are not part of a ranking, so a pending exclusive pick is dropped.
 * Returns null when the text is blank or already ranked.
 */
export function rankWithCustom(question: Question, ranked: string[], custom: string): { ranked: string[]; id: string } | null {
  const label = custom.trim();
  if (!label) return null;
  const exclusiveIds = new Set(question.options.filter((o) => o.exclusive).map((o) => o.id));
  const rankedIds = ranked.filter((id) => !exclusiveIds.has(id));
  const known = question.options.find((o) => !o.freeText && o.label.toLowerCase() === label.toLowerCase());
  const id = known?.id ?? label;
  if (rankedIds.includes(id)) return null;
  return { ranked: [...rankedIds, id], id };
}

export interface RankChipListProps {
  /** Fixed at #1 and not part of the answer (the main channel on "Any other channels?"). */
  pinned?: QuestionOption;
  /** Option ids (or custom labels) in order of importance. */
  ranked: readonly string[];
  /** The label of an id: a listed option's, else the id is the typed label itself. */
  labelOf: (id: string) => string;
  onRemove: (id: string) => void;
}

/**
 * The ranking as TP draws it: removable chips in tap order above the options, most important first. No reorder:
 * remove a chip and tap it again to move it. A pinned option leads as #1 with its hint and no way out, and the
 * ranked ones count from 2.
 */
export function RankChipList({ pinned, ranked, labelOf, onRemove }: RankChipListProps) {
  const first = pinned ? 2 : 1;
  return (
    <ol aria-label="Your ranking, most important first" className="flex flex-wrap gap-1.5">
      {pinned ? <RankChip rank={1} label={pinned.label} hint={pinned.hint} /> : null}
      {ranked.map((id, i) => (
        <RankChip key={id} rank={i + first} label={labelOf(id)} onRemove={() => onRemove(id)} />
      ))}
    </ol>
  );
}

/** A ranked pick: its number, the label, and a way out (the pinned main channel has none, only its hint). */
function RankChip({ rank, label, hint, onRemove }: { rank: number; label: string; hint?: string; onRemove?: () => void }) {
  return (
    <li className="flex h-7 items-center gap-1.5 rounded-md bg-ds-ink-a10 pl-1 pr-1.5 text-body-sm text-foreground animate-in fade-in duration-200 motion-reduce:animate-none">
      <span aria-hidden="true" className="flex size-5 items-center justify-center rounded-sm bg-ds-ink text-caption text-ds-paper">
        {rank}
      </span>
      <span className="sr-only">{rank}. </span>
      <span className="truncate">{label}</span>
      {onRemove ? (
        <IconButton label={`Remove ${label}`} icon={X} tone="ghost" size="sm" onClick={onRemove} />
      ) : hint ? (
        <span className="text-label text-ds-text-3">{hint}</span>
      ) : null}
    </li>
  );
}
